/**
 * obsClientLogger.ts — OBS: ingesta de eventos del FRONTEND al visor.
 *
 * Agrupa eventos (errores JS/React, warnings, hitos de UI) y los envía en lotes
 * a la Cloud Function `portal_ingestLogs`, que los persiste en `obs_events`.
 * Best-effort: nunca lanza; si falla el envío, se descarta (Analytics sigue
 * teniendo el registro). Sólo envía cuando hay usuario autenticado.
 *
 * REGLA: NO enviar PHI (nombres, cédula, diagnósticos, transcripción). Sólo
 * códigos, mensajes técnicos y stacks.
 */
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../config/firebase';

export type NivelObs = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'FATAL';

export interface ClientObsEvent {
  nivel: NivelObs;
  categoria: string;
  codigo: string;
  mensaje?: string;
  componente?: string;
  traceId?: string;
  consultaId?: string;
  salaId?: string;
  resultado?: string;
  detalle?: Record<string, unknown>;
  error?: { tipo?: string; mensaje?: string; stack?: string };
}

const ingestFn = httpsCallable<{ eventos: unknown[] }, { ok: boolean; escritos: number }>(
  functions,
  'portal_ingestLogs',
);

const FLUSH_MS = 8_000;
const MAX_BUFFER = 25;
const MAX_LOTE = 50;

const buffer: Record<string, unknown>[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush(): void {
  if (timer) return;
  timer = setTimeout(() => void flushObsClient(), FLUSH_MS);
}

/** Encola un evento de observabilidad del cliente. */
export function logClient(evento: ClientObsEvent): void {
  try {
    buffer.push({
      ...evento,
      // Hora del hecho: el buffer se vacía por lotes, así que sin esto todos los eventos de un
      // lote quedaban sellados con la misma hora de ingesta y sin orden entre ellos.
      ts: Date.now(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    });
    if (buffer.length >= MAX_BUFFER) void flushObsClient();
    else scheduleFlush();
  } catch {
    /* nunca romper al llamador */
  }
}

/** Envía el buffer acumulado (best-effort). */
export async function flushObsClient(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (buffer.length === 0) return;
  if (!auth.currentUser) return; // sólo autenticado; se reintenta en el próximo tick
  const lote = buffer.splice(0, MAX_LOTE);
  try {
    await ingestFn({ eventos: lote });
  } catch {
    /* best-effort: se descarta el lote */
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    void flushObsClient();
  });
}
